import axios from 'axios';
import {
  TaskSubmitResponse,
  APIResponse,
  VideoProcessingParams,
  Task,
  DeepSeekTranslationRequest,
  DeepSeekTranslationResponse,
  TTSRequest,
  TTSResponse,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

class APIService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP error! status: ${response.status}`,
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // 提交视频处理任务
  async submitTask(
    params: VideoProcessingParams
  ): Promise<APIResponse<TaskSubmitResponse>> {
    return this.request<TaskSubmitResponse>('/api/tasks', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // 获取任务列表
  async getTasks(): Promise<APIResponse<Task[]>> {
    return this.request<Task[]>('/api/tasks');
  }

  // 获取任务详情
  async getTask(taskId: string): Promise<APIResponse<Task>> {
    return this.request<Task>(`/api/tasks/${taskId}`);
  }

  // 取消任务
  async cancelTask(taskId: string): Promise<APIResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/api/tasks/${taskId}/cancel`, {
      method: 'POST',
    });
  }

  // 重试失败的任务
  async retryTask(taskId: string): Promise<APIResponse<TaskSubmitResponse>> {
    return this.request<TaskSubmitResponse>(`/api/tasks/${taskId}/retry`, {
      method: 'POST',
    });
  }

  // 批量提交任务
  async submitBatchTasks(
    paramsArray: VideoProcessingParams[]
  ): Promise<APIResponse<{ tasks: TaskSubmitResponse[] }>> {
    return this.request<{ tasks: TaskSubmitResponse[] }>('/api/tasks/batch', {
      method: 'POST',
      body: JSON.stringify(paramsArray),
    });
  }

  // 检查系统状态
  async healthCheck(): Promise<APIResponse<{ status: string }>> {
    return this.request<{ status: string }>('/api/health');
  }
}

export const apiService = new APIService();

// DeepSeek API 服务
class DeepSeekService {
  private apiKey: string;
  private baseURL: string = 'https://api.deepseek.com/v1';

  constructor() {
    this.apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY || '';
  }

  async translate(
    request: DeepSeekTranslationRequest
  ): Promise<DeepSeekTranslationResponse> {
    try {
      const response = await axios.post(
        `${this.baseURL}/chat/completions`,
        {
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `You are a professional translator specialized in translating Chinese to Arabic (UAE standard). 
Context: This is for mechanical tools video content.
Translate accurately while maintaining the natural flow and terminology appropriate for UAE audience.
Return only the translated text without any additional explanations or metadata.`,
            },
            {
              role: 'user',
              content: request.text,
            },
          ],
          temperature: 0.3,
          max_tokens: 2000,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );

      const translatedText = response.data.choices[0].message.content;

      return {
        translatedText: translatedText.trim(),
      };
    } catch (error) {
      console.error('DeepSeek translation failed:', error);
      throw new Error('Translation service failed');
    }
  }
}

export const deepSeekService = new DeepSeekService();

// TTS 服务
class TTSService {
  private apiKey: string;
  private baseURL: string;

  constructor() {
    this.apiKey = import.meta.env.VITE_TTS_API_KEY || '';
    this.baseURL = import.meta.env.VITE_TTS_API_URL || '';
  }

  async synthesize(request: TTSRequest): Promise<TTSResponse> {
    try {
      const response = await axios.post(
        `${this.baseURL}/synthesize`,
        {
          text: request.text,
          language: request.language,
          voice: request.voice || 'ar-AE-FemaleNeural',
          speed: request.speed || 1.0,
          pitch: request.pitch || 0,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );

      return {
        audioUrl: response.data.audioUrl,
        duration: response.data.duration,
        format: response.data.format || 'mp3',
      };
    } catch (error) {
      console.error('TTS synthesis failed:', error);
      throw new Error('TTS service failed');
    }
  }
}

export const ttsService = new TTSService();